import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

const POSTS_COLLECTION = 'posts';

/**
 * Map a Firestore document to the shape expected by BlogSection and BlogDetailPage.
 */
function mapPost(docSnap) {
  if (!docSnap || !docSnap.exists()) return null;
  const d = docSnap.data();
  const id = docSnap.id;
  const createdAt = d.createdAt?.toDate?.() || d.createdAt;
  const updatedAt = d.updatedAt?.toDate?.() || d.updatedAt;
  return {
    id,
    title: d.title || '',
    category: d.category || 'tech',
    excerpt: d.excerpt || '',
    content: d.content || '',
    tags: Array.isArray(d.tags) ? d.tags : [],
    published: d.published !== false,
    featuredImage: d.featuredImage || '',
    audioUrl: d.audioUrl || '', // Pre-generated TTS audio URL
    created_at: createdAt ? (typeof createdAt === 'object' && createdAt.toISOString ? createdAt.toISOString() : String(createdAt)) : new Date().toISOString(),
    updated_at: updatedAt ? (typeof updatedAt === 'object' && updatedAt.toISOString ? updatedAt.toISOString() : String(updatedAt)) : new Date().toISOString(),
  };
}

/**
 * Fetch all published blog posts from Firestore.
 * Returns [] if Firebase is not configured or on error.
 */
export async function fetchBlogs() {
  if (!db) return [];

  try {
    // First try with composite query (requires index)
    const q = query(
      collection(db, POSTS_COLLECTION),
      where('published', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(mapPost);
  } catch (err) {
    // If index not available, fallback to simple query and filter client-side
    console.warn('Firebase index not available, using fallback query:', err.message);
    try {
      const q = query(
        collection(db, POSTS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const allPosts = snapshot.docs.map(mapPost);
      return allPosts.filter(post => post.published === true);
    } catch (fallbackErr) {
      // Final fallback - get all docs without ordering
      console.warn('Fallback query failed, fetching all:', fallbackErr.message);
      try {
        const snapshot = await getDocs(collection(db, POSTS_COLLECTION));
        const allPosts = snapshot.docs.map(mapPost);
        return allPosts
          .filter(post => post.published === true)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } catch (finalErr) {
        console.error('Firebase fetchBlogs final error:', finalErr);
        return [];
      }
    }
  }
}

/**
 * Fetch ALL blog posts (including drafts) for admin.
 */
export async function fetchAllBlogs() {
  if (!db) return [];

  try {
    const q = query(
      collection(db, POSTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(mapPost);
  } catch (err) {
    // Fallback without ordering
    console.warn('fetchAllBlogs ordering failed, using fallback:', err.message);
    try {
      const snapshot = await getDocs(collection(db, POSTS_COLLECTION));
      const allPosts = snapshot.docs.map(mapPost);
      return allPosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } catch (finalErr) {
      console.error('Firebase fetchAllBlogs error:', finalErr);
      return [];
    }
  }
}

/**
 * Fetch a single blog post by id.
 * Returns null if not found or Firebase not configured.
 * Note: For public view, only returns published posts. Admin can view any post via direct Firestore access.
 */
export async function fetchBlogById(id, includeUnpublished = false) {
  if (!id) return null;
  
  // Check placeholder posts first
  const placeholder = getPlaceholderPost(id);
  if (placeholder) return placeholder;
  
  if (!db) return null;

  try {
    const docRef = doc(db, POSTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const post = mapPost(docSnap);
    
    // For public view, only return if published (unless includeUnpublished is true)
    if (!includeUnpublished && !post.published) {
      return null;
    }
    return post;
  } catch (err) {
    console.error('Firebase fetchBlogById error:', err);
    return null;
  }
}

/**
 * Create a new blog post
 */
export async function createBlogPost(postData) {
  if (!db) throw new Error('Firebase not configured');

  try {
    const newPost = {
      title: postData.title || '',
      category: postData.category || 'tech',
      excerpt: postData.excerpt || '',
      content: postData.content || '',
      tags: postData.tags || [],
      published: postData.published || false,
      featuredImage: postData.featuredImage || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, POSTS_COLLECTION), newPost);
    return { id: docRef.id, ...newPost };
  } catch (err) {
    console.error('Firebase createBlogPost error:', err);
    throw err;
  }
}

/**
 * Update an existing blog post
 */
export async function updateBlogPost(id, postData) {
  if (!db) throw new Error('Firebase not configured');

  try {
    const docRef = doc(db, POSTS_COLLECTION, id);
    const updateData = {
      ...postData,
      updatedAt: serverTimestamp(),
    };
    
    // Remove undefined fields
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(docRef, updateData);
    return { id, ...updateData };
  } catch (err) {
    console.error('Firebase updateBlogPost error:', err);
    throw err;
  }
}

/**
 * Delete a blog post
 */
export async function deleteBlogPost(id) {
  if (!db) throw new Error('Firebase not configured');

  try {
    const docRef = doc(db, POSTS_COLLECTION, id);
    await deleteDoc(docRef);
    return true;
  } catch (err) {
    console.error('Firebase deleteBlogPost error:', err);
    throw err;
  }
}

/**
 * Toggle publish status
 */
export async function togglePublishStatus(id, currentStatus) {
  return updateBlogPost(id, { published: !currentStatus });
}

/** Placeholder posts for demo (Lorem Ipsum). */
export const LOREM_PLACEHOLDER_POSTS = [];

export function getPlaceholderPost(id) {
  return LOREM_PLACEHOLDER_POSTS.find((p) => p.id === id) || null;
}

/**
 * Shape for creating/updating a post (Firestore fields).
 */
function toFirestorePost(data) {
  return {
    title: data.title || '',
    category: data.category || 'tech',
    excerpt: data.excerpt || '',
    content: data.content || '',
    tags: Array.isArray(data.tags) ? data.tags : (typeof data.tags === 'string' ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : []),
    published: data.published !== false,
    updatedAt: serverTimestamp(),
  };
}

/**
 * Create a new post in Firestore. Sets createdAt and updatedAt.
 * Returns the new document id, or null on error.
 */
export async function createPost(post) {
  if (!db) return null;
  try {
    const ref = await addDoc(collection(db, POSTS_COLLECTION), {
      ...toFirestorePost(post),
      createdAt: serverTimestamp(),
    });
    return ref.id;
  } catch (err) {
    console.error('Firebase createPost error:', err);
    return null;
  }
}

/**
 * Update an existing post. Only updates updatedAt (not createdAt).
 */
export async function updatePost(id, post) {
  if (!db || !id) return false;
  try {
    const docRef = doc(db, POSTS_COLLECTION, id);
    await updateDoc(docRef, toFirestorePost(post));
    return true;
  } catch (err) {
    console.error('Firebase updatePost error:', err);
    return false;
  }
}

/**
 * Delete a post by id.
 */
export async function deletePost(id) {
  if (!db || !id) return false;
  try {
    await deleteDoc(doc(db, POSTS_COLLECTION, id));
    return true;
  } catch (err) {
    console.error('Firebase deletePost error:', err);
    return false;
  }
}

/**
 * Fetch all posts (including drafts) for admin. No published filter.
 * Requires caller to be authenticated (Firestore rules enforce).
 */
export async function fetchAllPosts() {
  if (!db) return [];
  try {
    const q = query(
      collection(db, POSTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(mapPost);
  } catch (err) {
    console.error('Firebase fetchAllPosts error:', err);
    return [];
  }
}
